<div class="flex flex-col gap-3">
  <p-toast></p-toast>
  <p-confirmdialog />

  <div class="flex justify-between items-center">
    <h3 class="text-base font-semibold m-0">
      {{ 'catalog.openingHours.title' | translate }}
    </h3>
    <p-button
      size="small"
      icon="fa fa-plus"
      [label]="'catalog.openingHours.add' | translate"
      (onClick)="openCreateDialog()"
    />
  </div>

  @if (sortedHours().length === 0) {
    <p class="text-sm text-color-secondary italic">
      {{ 'catalog.openingHours.empty' | translate }}
    </p>
  } @else {
    <p-table [value]="sortedHours()" styleClass="p-datatable-sm" dataKey="id">
      <ng-template #header>
        <tr>
          <th>{{ 'catalog.openingHours.fields.type' | translate }}</th>
          <th>{{ 'catalog.openingHours.fields.dateStart' | translate }}</th>
          <th>{{ 'catalog.openingHours.fields.dateEnd' | translate }}</th>
          <th>{{ 'catalog.openingHours.fields.notes' | translate }}</th>
          <th class="w-24"></th>
        </tr>
      </ng-template>
      <ng-template #body let-row>
        <tr>
          <td>
            <p-tag
              [value]="('catalog.openingHours.types.' + row.type) | translate"
              severity="info"
            />
          </td>
          <td>{{ row.date_start | date: 'dd.MM.yyyy' : 'Europe/Berlin' }}</td>
          <td>
            {{ row.date_end ? (row.date_end | date: 'dd.MM.yyyy' : 'Europe/Berlin') : '—' }}
          </td>
          <td class="max-w-md truncate">{{ row.notes }}</td>
          <td class="text-right">
            <div class="flex items-center justify-end gap-2">
              <button
                pButton
                type="button"
                icon="fa fa-pencil"
                text
                rounded
                severity="secondary"
                (click)="openEditDialog(row)"
              ></button>
              <button
                pButton
                type="button"
                icon="fa fa-trash"
                text
                rounded
                severity="secondary"
                (click)="onRowDelete($event, row)"
              ></button>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  }

  <p-dialog
    [header]="
      editing()
        ? ('catalog.openingHours.edit' | translate)
        : ('catalog.openingHours.add' | translate)
    "
    [modal]="true"
    [visible]="dialogVisible()"
    (visibleChange)="dialogVisible.set($event)"
    [closable]="!submitting()"
    styleClass="w-[95vw] max-w-3xl"
    (onHide)="closeDialog()"
  >
    <form
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
      class="flex flex-col gap-4"
    >
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div class="flex flex-col">
          <label class="text-sm mb-1">
            {{ 'catalog.openingHours.fields.type' | translate }}
          </label>
          <p-select
            formControlName="type"
            [options]="typeOptions"
            optionValue="value"
            (onChange)="onTypeChange($event.value)"
            appendTo="body"
          >
            <ng-template let-opt pTemplate="item">
              {{ opt.label | translate }}
            </ng-template>
            <ng-template let-opt pTemplate="selectedItem">
              {{ opt.label | translate }}
            </ng-template>
          </p-select>
        </div>

        <div class="flex flex-col">
          <label class="text-sm mb-1">
            {{ 'catalog.openingHours.fields.dateStart' | translate }}
          </label>
          <p-datepicker
            formControlName="date_start"
            dateFormat="dd.mm.yy"
            appendTo="body"
          />
          @if (
            form.get('date_start')?.touched && form.get('date_start')?.invalid
          ) {
            <small class="text-red-500 mt-1">
              {{ 'validation.required' | translate }}
            </small>
          }
        </div>

        <div class="flex flex-col">
          <label class="text-sm mb-1">
            {{ 'catalog.openingHours.fields.dateEnd' | translate }}
          </label>
          <p-datepicker
            formControlName="date_end"
            dateFormat="dd.mm.yy"
            appendTo="body"
          />
        </div>
      </div>

      @if (showRangeTimes()) {
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div class="flex flex-col">
            <label class="text-sm mb-1">
              {{ 'catalog.openingHours.fields.timeStart' | translate }}
            </label>
            <p-datepicker
              formControlName="time_start"
              [timeOnly]="true"
              hourFormat="24"
              appendTo="body"
            />
          </div>
          <div class="flex flex-col">
            <label class="text-sm mb-1">
              {{ 'catalog.openingHours.fields.timeEnd' | translate }}
            </label>
            <p-datepicker
              formControlName="time_end"
              [timeOnly]="true"
              hourFormat="24"
              appendTo="body"
            />
          </div>
        </div>
      }

      @if (showWeekdays()) {
        <div class="border rounded p-3 surface-border">
          <h4 class="text-sm font-semibold mb-3">
            {{ 'catalog.openingHours.weekdaysHeader' | translate }}
          </h4>
          <div class="flex flex-col gap-3">
            @for (dow of weekdays; track dow) {
              <div class="flex items-center gap-2">
                <span class="w-12 text-sm">
                  {{ ('catalog.openingHours.weekdays.' + dow) | translate }}
                </span>
                <p-datepicker
                  [formControlName]="'time_start_' + dow"
                  [timeOnly]="true"
                  hourFormat="24"
                  appendTo="body"
                  styleClass="flex-1"
                />
                <span class="text-sm text-color-secondary">–</span>
                <p-datepicker
                  [formControlName]="'time_end_' + dow"
                  [timeOnly]="true"
                  hourFormat="24"
                  appendTo="body"
                  styleClass="flex-1"
                />
              </div>
            }
          </div>
        </div>
      }

      <div class="flex flex-col">
        <label class="text-sm mb-1">
          {{ 'catalog.openingHours.fields.notes' | translate }}
        </label>
        <textarea pTextarea formControlName="notes" rows="2"></textarea>
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <p-button
          type="button"
          severity="secondary"
          [label]="'catalog.openingHours.cancel' | translate"
          [disabled]="submitting()"
          (onClick)="closeDialog()"
        />
        <p-button
          type="submit"
          [label]="'catalog.openingHours.save' | translate"
          [loading]="submitting()"
        />
      </div>
    </form>
  </p-dialog>
</div>
